import { useReactiveVar } from '@apollo/client';
import { Stack } from '@fluentui/react';
import {
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  Edge,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowProvider,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  CustomReactFlowControls,
  setInteractive,
} from 'common/components/CustomReactFlowControls';
import { DEFAULT_NODE_HEIGHT } from 'common/components/PreviewApprovalHierarchyReactFlow/constants';
import {
  buildHierarchyTree,
  getLayoutedElements,
} from 'common/components/PreviewApprovalHierarchyReactFlow/utils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PreviewApprovalTesting_previewApprovalTesting_approvalMapTypes } from '../../../__generated__/PreviewApprovalTesting';

export interface HierarchyNode
  extends PreviewApprovalTesting_previewApprovalTesting_approvalMapTypes {
  children?: HierarchyNode[];
  width?: number;
  height?: number;
}

interface GenericHierarchyFlowProps {
  defaultNodeHeight?: number;
  height?: number;
  width?: number;
  onNodeData?: (nodeData: HierarchyNode | undefined) => void;
  NodeViewComponent: React.ComponentType<{ data: HierarchyNode; key: string }>;
  rawData: HierarchyNode[];
}

export const TestHierarchyFlow = ({
  height,
  defaultNodeHeight,
  width,
  rawData,
  onNodeData,
  NodeViewComponent,
}: GenericHierarchyFlowProps) => {
  type FlowNodeData = HierarchyNode & { [key: string]: unknown };
  const [rfNodes, setRfNodes] = useState<Node<FlowNodeData>[]>([]);
  const [rfEdges, setRfEdges] = useState<Edge[]>([]);
  const isInteractive = useReactiveVar(setInteractive);

  const onNodesChange = useCallback(
    (changes: any) => setRfNodes((nds) => applyNodeChanges(changes, nds)),
    []
  );

  const onEdgesChange = useCallback(
    (changes: any) => setRfEdges((eds) => applyEdgeChanges(changes, eds)),
    []
  );

  useEffect(() => {
    if (rawData?.length) {
      const nodesWithChildren = rawData.map((item: HierarchyNode) => ({
        ...item,
        children: [],
        level: item.level ?? 0,
        width: 300,
        height: 200,
      }));

      const roots = buildHierarchyTree(nodesWithChildren);
      const newNodes: Node<FlowNodeData>[] = [];
      const newEdges: Edge[] = [];

      const flatten = (node: HierarchyNode) => {
        if (!node.id) return;

        newNodes.push({
          id: node.id,
          data: node as FlowNodeData,
          type: 'hierarchyNode',
          position: { x: 0, y: 0 },
          style: {
            zIndex: 1,
            pointerEvents: 'all',
          },
        });

        if (node.children && node.children.length > 0) {
          node.children.forEach((child) => {
            if (child.id && node.id) {
              newEdges.push({
                id: `${node.id}-${child.id}`,
                source: node.id,
                target: child.id,
                type: 'step',
                animated: true,
              });
              flatten(child);
            }
          });
        }
      };

      roots.forEach(flatten);

      const { nodes: layouted, edges: layoutedEdges } = getLayoutedElements(
        newNodes,
        newEdges,
        {
          direction: 'TB',
          defaultNodeHeight: defaultNodeHeight
            ? defaultNodeHeight
            : DEFAULT_NODE_HEIGHT,
        }
      );
      setRfNodes(layouted);
      setRfEdges(layoutedEdges);

      if (onNodeData && rawData[0]) {
        onNodeData(rawData[0]);
      }
    }
  }, [onNodeData]);

  const nodeTypes: NodeTypes = useMemo(
    () => ({
      hierarchyNode: (props) => (
        <NodeViewComponent data={props.data} key={props.id} />
      ),
    }),
    [NodeViewComponent]
  );

  return (
    <ReactFlowProvider>
      <Stack style={{ alignContent: 'center' }}>
        <div
          style={{
            height: height ? height : 600,
            width: width ? width : 1200,
          }}
        >
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            proOptions={{ hideAttribution: true }}
            panOnScroll
            fitView
            minZoom={0.1}
            panOnDrag={isInteractive}
            nodesDraggable={isInteractive}
            nodesConnectable={isInteractive}
            elementsSelectable={isInteractive}
            zoomOnScroll={isInteractive}
            zoomOnPinch={isInteractive}
            zoomOnDoubleClick={isInteractive}
          >
            <Background />
            <CustomReactFlowControls />
          </ReactFlow>
        </div>
      </Stack>
    </ReactFlowProvider>
  );
};
